/**
 * Card — a compact, optionally-clickable surface for list/grid entries.
 *
 * Use for repeated tiles: party members, jobs, talents, mode cards. When
 * `interactive` (or `onClick`) is set, the card lifts + brightens its border
 * on hover via the local style-swap pattern (jsdom-safe, no CSS injection).
 */
import React, { useState, type ReactNode } from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  SHADOW,
  DURATION,
  EASING,
} from '../theme';

export interface CardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  interactive?: boolean;
  children?: ReactNode;
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.md,
};

const titleColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: SPACE.xxs,
  minWidth: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.lg,
  fontWeight: FONT_WEIGHT.semibold,
  color: COLORS.textPrimary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.sm,
  color: COLORS.textMuted,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const iconStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: FONT_SIZE.xl,
};

export function Card({
  title,
  subtitle,
  icon,
  onClick,
  interactive,
  children,
}: CardProps): React.ReactElement {
  const [hovered, setHovered] = useState(false);
  const clickable = interactive === true || onClick != null;

  const cardStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.md,
    padding: SPACE.lg,
    background: COLORS.surfaceRaised,
    border: `1px solid ${hovered && clickable ? COLORS.borderStrong : COLORS.borderSubtle}`,
    borderRadius: RADII.lg,
    cursor: clickable ? 'pointer' : 'default',
    transition: `all ${DURATION.base} ${EASING.standard}`,
    transform: hovered && clickable ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: hovered && clickable ? SHADOW.md : 'none',
  };

  const hasHeader = title != null || subtitle != null || icon != null;

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...(clickable ? { role: 'button' as const, tabIndex: 0 } : {})}
    >
      {hasHeader && (
        <div style={headerStyle}>
          {icon != null && <span style={iconStyle}>{icon}</span>}
          {(title != null || subtitle != null) && (
            <div style={titleColStyle}>
              {title != null && <span style={titleStyle}>{title}</span>}
              {subtitle != null && <span style={subtitleStyle}>{subtitle}</span>}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
